import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {confirmDialog, ConfirmDialog} from "primereact/confirmdialog";

import {AppContext} from "../../HomePage";
import {ApiKeyService} from "../../service/ApiKeyService";
import {user} from "../../utils/Utils";
import ApiDocDialog from "./ApiDoc";

// The cached user snapshot in localStorage can be stale if an admin toggles
// api_access_enabled while the customer is logged in. It is only used as the
// initial guess for what to render while loading; a 403 from GET /api_key/ is
// the authoritative source of truth and overrides it.
const ApiAccess = () => {

    const {t, toast, setError} = React.useContext(AppContext);

    const account = user() || {};
    const cachedEntitled = !!(account.settings && account.settings.api_access_enabled);

    const [entitled, setEntitled] = useState(cachedEntitled);
    const [apiKey, setApiKey] = useState(null);
    const [revealed, setRevealed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [docVisible, setDocVisible] = useState(false);
    // Distinct from `entitled`: `entitled` means "this session may manage the
    // key", which is also true for an impersonating admin. `accessEnabled` is
    // the account's own flag, and is what tells the admin the account is not
    // switched on yet.
    const [accessEnabled, setAccessEnabled] = useState(cachedEntitled);

    const apiKeyService = new ApiKeyService();

    useEffect(() => {
        apiKeyService.getApiKey()
            .then(data => {
                // A 200 means the caller may manage this key - either an
                // entitled customer, or an admin impersonating one.
                setEntitled(true);
                setApiKey(data.api_key);
                setAccessEnabled(data.api_access_enabled);
            })
            .catch(err => {
                if (err.response && err.response.status === 403) {
                    setEntitled(false);
                } else {
                    setError(err);
                }
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generate = () => {
        apiKeyService.generateApiKey().then(data => {
            setApiKey(data.api_key);
            setAccessEnabled(data.api_access_enabled);
            setRevealed(true);
            toast.current.show({severity: 'success', summary: t('api_access.generated'),
                                detail: t('message_detail.successful')});
        }).catch(err => setError(err));
    };

    const confirmRegenerate = () => confirmDialog({
        message: t('api_access.regenerate_warning'),
        header: t('api_access.regenerate'),
        icon: 'pi pi-exclamation-triangle',
        accept: generate
    });

    const confirmRevoke = () => confirmDialog({
        message: t('api_access.revoke_warning'),
        header: t('api_access.revoke'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => apiKeyService.revokeApiKey().then(data => {
            setApiKey(null);
            setAccessEnabled(data.api_access_enabled);
            setRevealed(false);
            toast.current.show({severity: 'success', summary: t('api_access.revoked'),
                                detail: t('message_detail.successful')});
        }).catch(err => setError(err))
    });

    const copy = () => {
        navigator.clipboard.writeText(apiKey.key);
        toast.current.show({severity: 'success', summary: t('api_access.copied'),
                            detail: t('message_detail.successful')});
    };

    const masked = () => '•'.repeat(24);

    const keyBody = () => {
        if (loading) return <i className="pi pi-spin pi-spinner"/>;
        if (!entitled) return <Message severity="info" text={t('api_access.not_entitled')}/>;
        if (!apiKey) {
            return <React.Fragment>
                <p className="acc-section-sub">{t('api_access.no_key_yet')}</p>
                {!accessEnabled && (
                    <Message severity="warn" className="api-grant-note"
                             text={t('api_access.admin_will_enable')}/>
                )}
                <Button label={t('api_access.generate')} icon="pi pi-plus"
                        className="p-button-outlined p-button-success" onClick={generate}/>
            </React.Fragment>;
        }
        return <React.Fragment>
            <div className="api-key-row">
                <InputText className="api-key-input" readOnly
                           value={revealed ? apiKey.key : masked()}/>
                <Button icon={revealed ? "pi pi-eye-slash" : "pi pi-eye"}
                        tooltip={revealed ? t('api_access.hide') : t('api_access.show')}
                        className="p-button-text" onClick={() => setRevealed(!revealed)}/>
                <Button icon="pi pi-copy" tooltip={t('api_access.copy')}
                        className="p-button-text" onClick={copy}/>
                <Button icon="pi pi-refresh" tooltip={t('api_access.regenerate')}
                        className="p-button-text" onClick={confirmRegenerate}/>
                <Button icon="pi pi-trash" tooltip={t('api_access.revoke')}
                        className="p-button-text p-button-danger" onClick={confirmRevoke}/>
            </div>
            <div className="api-key-meta">
                <span><b>{t('api_access.created')}:</b> {apiKey.created || '-'}</span>
                <span><b>{t('api_access.last_used')}:</b> {apiKey.last_used_at || '-'}</span>
            </div>
        </React.Fragment>;
    };

    return <React.Fragment>
        <ConfirmDialog/>
        <div className="acc-section">
            <p className="acc-section-title">{t('api_access.title')}</p>
            <p className="acc-section-sub">{t('api_access.sub')}</p>
            {keyBody()}
        </div>
        <div className="acc-section">
            <p className="acc-section-title">{t('api_doc.title')}</p>
            <p className="acc-section-sub">{t('api_doc.intro')}</p>
            <Button label={t('api_doc.open')} icon="pi pi-book"
                    className="p-button-outlined" onClick={() => setDocVisible(true)}/>
        </div>
        <ApiDocDialog visible={docVisible} onHide={() => setDocVisible(false)}/>
    </React.Fragment>;
};

export default ApiAccess;
